import React, { useState, useEffect, useRef, useMemo } from "react";
import { Typography } from "@mui/material";
import "./Resources.scss";
import { useQuery } from "@apollo/client/react";
import { useFetchResourcesViaId } from "../../hooks/useFetchResourcesViaId";
import { useFetchResourceType } from "../../hooks/useFetchResourceType";
import {  isSafeUrl, sanitizeFileName } from "../../utils/utils";
import Breadcrumbs from "../../components/Breadcrumbs";
import ScrollToHashElement from "../../components/ScrollToHashElement";
import {
  REACT_APP_WUCA_DXP_ADMIN_URL,
  REACT_APP_WUCA_DXP_API_BEARER_TOKEN,
  REACT_APP_WUCA_PUBLIC_URL,
} from "../../services/config";
import { getLocalizedValueSync } from "../../services/localizationService";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Asset_Category_Query,
  RESOURCE_ASSET_QUERY,
  RESOURCE_ASSET_QUERY_ALL,
  Resource_Folder_Query,
  Resource_Folder_Query_ALL,
} from "../../services/query";
import ResourcePreviewDialog from "../../components/ResourcePreviewDialog";
import ResourceAccordion from "../../components/ResourceAccordion";
import {
  IAssetsCategoryData,
  CampaignItemsRecords,
  CampaignItemsResourceType,
  AssetsCategoryItem,
  previewFileTypes,
  ResourceApiResponse,
  mimeTypes,
  ResourceAssetItem,
  ResourceFolderItem,
  FileItem,
} from "../../context/model";
import axios from "axios";
import { useAppContext } from "../../provider/AppContextProvider";
import NotFound from "../NotFound";
import { fetchAllData } from "../../services/fetchAllData";
import { useFetchAllRecords } from "../../hooks/useFetchAllRecords";
import AppSnackbar from "../../components/AppSnackbar";
import { DEFAULT_MESSAGES, SnackbarSeverity } from "../../components/AppSnackbar/AppSnackbar";
import { getAllResources, getResourceById } from "../../services/resourcesService";
import { logger } from "../../services/logger";
import { useSilentTokenAcquire } from "../../hooks/useSilentTokenAcquire";
import DOMPurify from "dompurify";



interface ChildFolder {
  folderName: string;
  orderBy: number;
  files: FileItem[];
}

interface ParentFolder {
  parentFolder: string;
  orderBy: number;
  childFolders: ChildFolder[];
}
const MAX_RELOGIN_ATTEMPTS = 2;


const Resources = () => {

  const [resourceCampaignAllRecord, setResourceCampaignAllRecord] = useState<
    CampaignItemsRecords[]
  >([]);
  const [resourceTypeAllRecord, setResourceTypeAllRecord] = useState<
    CampaignItemsResourceType[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [resourceId, setResourceId] = useState<string | null>(null);
  const [resourceTypeId, setResourceTypeId] = useState<string | null>(null);
  const { isInitializing, updateLoading } = useAppContext();
  const [assetApiData, setAssetApiData] = useState<ResourceApiResponse>([]);

  const calledOnceRef = useRef(false);

  const { resourceAliasPath } = useParams<{ resourceAliasPath: string }>();
  const { resourceAliasPath1 } = useParams<{ resourceAliasPath1: string }>();
  const navigate = useNavigate();

  const handleGoHome = () => navigate("/");

  const { resourceAllRecord, isResourceCalled } = useFetchResourcesViaId(
    `/${resourceAliasPath}/${resourceAliasPath1}` || ""
  );
  const { resourceTypeRecord } = useFetchResourceType(resourceTypeId || "");

  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [previewFile, setPreviewFile] = useState<FileItem>({
    fileName: "",
    filePath: "",
    fileType: "",
    fileid: "",
  });
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState<SnackbarSeverity>("success");
  const [messageSnackbar, setMessageSnackbar] = useState<string | undefined>(undefined);
  const silentTokenAcquireHook = useSilentTokenAcquire(); // <-- Call hook here

  const reloginAttemptsRef = useRef(0);

  const showSnackbar = (sev: SnackbarSeverity, msg?: string) => {
    setSeverity(sev);
    setMessageSnackbar(msg ?? DEFAULT_MESSAGES[sev]);
    setOpenSnackbar(true);
  };

  const handleSnackbarClose = (_e?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") return;
    setOpenSnackbar(false);
  };

  const localizedDownloadMessage =
    getLocalizedValueSync("CA.resource.downloadall.message") || "";



  const getResourceByIdData = async (file: FileItem, contentType: string, contentDisposition: string) => {
    try {
      const response: any = await getResourceById(file.fileid, contentType, contentDisposition);
      reloginAttemptsRef.current = 0; // Reset on success
      return response.data || "";
    } catch (error: any) {
      console.info("Error fetching file:", error);
      if (
        (error?.response?.status === 401 || error?.response?.status === 403) &&
        reloginAttemptsRef.current < MAX_RELOGIN_ATTEMPTS
      ) {
        reloginAttemptsRef.current++;
        const isReLogined = await silentTokenAcquireHook();
        if (isReLogined) {
          const response: any = await getResourceById(file.fileid, contentType, contentDisposition);
          return response.data || "";
        }
      } else {
        reloginAttemptsRef.current = 0; // Reset on other errors
        return "";
      }
    }
  };


  const handlePreviewFile = async (file: FileItem) => {
    const baseName = sanitizeFileName(file.fileName || "download");
    const extension = sanitizeFileName(file.filePath.split('.').pop() || "bin");
    const safeFileName = `${baseName}.${extension}`;
    const fileWithSafeName = { ...file, safeFileName };
    try {
      setLoading(true);
      const responseData:string = await getResourceByIdData(file, file.fileType, "inline");
      if (responseData && isSafeUrl(responseData)) {
        setBlobUrl(responseData);
        setPreviewFile(fileWithSafeName);
        setOpen(true);
      } else {
        showSnackbar("error", "The file could not be previewed. Please try again.");
      }
    } catch (error) {
      console.info("Error fetching file:", error);
    } finally {
      setLoading(false);
    }
  };


  const handleDownloadFile = async (file: FileItem) => {
    const baseName = sanitizeFileName(file.fileName || "download");
    const extension = sanitizeFileName(file.filePath.split('.').pop() || "bin");
    const safeFileName = sanitizeFileName(file.filePath.split('.').pop() ? file.filePath : `${baseName}.${extension}`);

    showSnackbar("info", "Download in progress... Please wait. Do not close or refresh the window.");
    try {
      const responseData:string = await getResourceByIdData(file, "application/octet-stream", `attachment; filename="${safeFileName}"`);
      if (responseData && isSafeUrl(responseData)) {
        const link = document.createElement("a");
        link.href = responseData;       // ✅ safe now
        link.download = safeFileName;   // already sanitized earlier
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        (window as any).adobeDataLayer = (window as any).adobeDataLayer || [];
        const adobeEvent = {
          event: "download",
          document: {
            filename: safeFileName,
            title: file.fileName,
          },
        };
        (window as any).adobeDataLayer.push(adobeEvent);
      } else {
        console.log("Invalid or unsafe URL for download:");
        showSnackbar("error", "The file could not be downloaded. Please try again.");
      }
    } catch (error) {
      console.info("Download failed:", error);
    }
  };
  const handleClosePreview = () => {
    if (blobUrl) {
      // URL.revokeObjectURL(blobUrl);
    }
    setBlobUrl(null);
    setOpen(false);
  };


  const {
    error: assetError,
    allRecords: assetData,
    loading: assetLoading,
  } = useFetchAllRecords(RESOURCE_ASSET_QUERY_ALL, "cAResourceAssetCollection", useMemo(() => ({}), []));
  const {
    error: folderError,
    allRecords: folderData,
    loading: folderLoading,
  } = useFetchAllRecords(Resource_Folder_Query_ALL, "cAResourceFolderCollection", useMemo(() => ({}), []));


  const {
    error: assetCategoryError,
    data: assetCategoryData,
    loading: assetCategoryLoading,
  } = useQuery<IAssetsCategoryData>(Asset_Category_Query, {
    skip: isInitializing,
  });
  const groupAssets = (
    assets: ResourceAssetItem[] | undefined,
    folders: ResourceFolderItem[] | undefined,
    categories: AssetsCategoryItem[] | undefined
  ): ParentFolder[] => {

    if (!folders || !categories) {
      return [];
    }
    return folders
      .map((folder) => {
        const childFolders = categories
          .map((category) => ({
            categoryName: category.categoryName,
            orderBy: category.orderBy,
            categoryId: category._system?.id || "",
          }))
          .sort((a, b) => {
            if (a.orderBy == null && b.orderBy == null) {
              return a.categoryName.localeCompare(b.categoryName); // Both null → sort by name
            }
            if (a.orderBy === null) return 1;
            if (b.orderBy === null) return -1;
            if (a.orderBy !== b.orderBy) {
              return a.orderBy - b.orderBy;
            }
            return a.categoryName.localeCompare(b.categoryName); // If orderBy is the same, sort by name
          })
          .map((category) => {
            const files = assets?.filter(
              (asset) =>
                asset.resourceFolderName === folder._system?.id &&
                asset.assetCategory === category.categoryId &&
                asset.resource === resourceId
            );
            return {
              folderName: category.categoryName,
              orderBy: category.orderBy,
              files:
                files
                  ?.map((file) => ({
                    fileName: file.title,
                    fileid: file._system?.id || "",
                    filePath:
                      file.imageContentHub?.items?.[0]?.contentFile?.name || "",
                    fileType:
                      file.imageContentHub?.items?.[0]?.contentFile?.fileType ||
                      "",
                    externalURL: file.externalURL || "",
                  }))
                  .sort((a, b) => a.fileName.localeCompare(b.fileName)) || [],
            };
          })
          .filter((childFolder) => childFolder.files.length > 0);
        return {
          parentFolder: folder.folderName,
          orderBy: folder.orderBy,
          childFolders,
        };
      })
      .filter((parentFolder) => parentFolder.childFolders.length > 0)
      .sort((a, b) => {
        if (a.orderBy == null && b.orderBy == null) {
          return a.parentFolder.localeCompare(b.parentFolder); // Both null → sort by name
        }
        if (a.orderBy === null) return 1;
        if (b.orderBy === null) return -1;
        if (a.orderBy !== b.orderBy) {
          return a.orderBy - b.orderBy;
        }
        return a.parentFolder.localeCompare(b.parentFolder); // If orderBy is the same, sort by name
      });
  };

  const mappedParentFolderData = [
    ...groupAssets(
      assetData,
      folderData,
      assetCategoryData?.cAAssetCategoryCollection?.items
    ),
  ];

  const [parentExpanded, setParentExpanded] = useState<String[]>(
    ["panel-0"]
  );
  const [childExpanded, setChildExpanded] = useState<String[]>(
    ["child-panel-0-0"]
  );
  const ScrollToElement = (isExpanded: boolean, panel: string) => {
    if (isExpanded && panel) {
      const isMobile = window.innerWidth <= 768;
      const effectiveOffset = isMobile ? 85 : 172;
      setTimeout(() => {
        const element = document.getElementById(panel);
        if (element) {
          const elementPosition =
            element.getBoundingClientRect().top + window.scrollY;
          window.scrollTo({
            top: elementPosition - effectiveOffset,
            behavior: "smooth",
          });
        }
      }, 500);
    }
  }

  const handleParentAccordionChange =
    (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      if (parentExpanded.includes(panel) && !isExpanded) {
        setParentExpanded(prev => prev.filter(p => p !== panel));

      } else {
        if (isExpanded) {
          setParentExpanded([panel]);
        }
      }


      ScrollToElement(isExpanded, panel);
    };

  const handleChildAccordionChange =
    (panel: string) => (_event: React.ChangeEvent<{}>, isExpanded: boolean) => {
      if (parentExpanded.includes(panel) && !isExpanded) {
        setChildExpanded(prev => prev.filter(p => p !== panel));
      } else {
        if (isExpanded) {
          setChildExpanded([panel]);
        }
      }
      ScrollToElement(isExpanded, panel);
    };

  const location = useLocation();
  const clickedAssetId = location.state?.clickedAssetId || null;

  useEffect(() => {
    if (clickedAssetId && mappedParentFolderData.length > 0) {
      mappedParentFolderData.forEach((parent, i) => {
        parent.childFolders.forEach((child, j) => {
          const assetFound = child.files.some((file) =>
            file.fileid.includes(clickedAssetId)
          );
          if (assetFound) {
            setParentExpanded([`panel-${i}`]);
            setChildExpanded([`child-panel-${i}-${j}`]);
          } else {
            console.log("No matching asset found for Clicked Asset ID:");
          }
        });
      });
      location.state.clickedAssetId = null;
    }
  }, [clickedAssetId, mappedParentFolderData]);

  // Add useEffect to process resourceAllRecord data
  useEffect(() => {
    if (resourceAllRecord && resourceAllRecord.length > 0) {
      const resource = resourceAllRecord[0];
      // Set resource campaign record
      const campaignRecord: CampaignItemsRecords = {
        resourceId: resource._system?.id || '',
        resourceTitle: resource.title || '',
        resourceDesc: resource.shortDescription || '',
        resourceImage:
          resource?.imageContentHub?.items &&
            resource.imageContentHub?.items?.length > 0
            ? `${REACT_APP_WUCA_DXP_ADMIN_URL}${resource?.imageContentHub?.items?.[0]?.contentFile?.path}`
            : `${REACT_APP_WUCA_PUBLIC_URL}/image_not_available.png`,
        resourceTypeId: resource.resourceType?.items?.[0]?._system?.id || ''
      };
      setResourceCampaignAllRecord([campaignRecord]);

      // Set resource ID for asset fetching
      setResourceId(resource._system?.id || '');

      // Set resource type ID if available
      const resourceTypeId = resource.resourceType?.items?.[0]?._system.id;
      if (resourceTypeId) {
        setResourceTypeId(resourceTypeId);
      }
    }
  }, [resourceAllRecord]);

  // Add useEffect to process resourceTypeRecord data
  useEffect(() => {
    if (resourceTypeRecord && resourceTypeRecord.length > 0) {
      const resourceType = resourceTypeRecord[0];

      const resourceTypeData: CampaignItemsResourceType = {
        resourceTypeId: resourceType._system?.id || '',
        resourceTypeTitle: resourceType.title || '',
        resourceTypeImage: resourceType?.imageContentHub?.items &&
          resourceType.imageContentHub?.items?.length > 0
          ? `${REACT_APP_WUCA_DXP_ADMIN_URL}${resourceType?.imageContentHub?.items?.[0]?.contentFile?.path}`
          : `${REACT_APP_WUCA_PUBLIC_URL}/image_not_available.png`
      };
      setResourceTypeAllRecord([resourceTypeData]);
    }
  }, [resourceTypeRecord]);

  return (
    <>
      {resourceAllRecord.length > 0 ? (
        <>
          <div className="campaign-page">
            <Breadcrumbs current={resourceCampaignAllRecord[0]?.resourceTitle || resourceAllRecord[0]?.title} />
            <div className="campaign-page-section">
              <div className="campaign-page-left-section">
                <Typography variant="h2" className="campaign-header">
                  {resourceCampaignAllRecord[0]?.resourceTitle || resourceAllRecord[0]?.title}
                </Typography>
                <Typography variant="body2" className="campaign-header-content">
                  {localizedDownloadMessage}
                </Typography>
                <ResourceAccordion
                  data={mappedParentFolderData}
                  parentExpanded={parentExpanded}
                  childExpanded={childExpanded}
                  onParentChange={handleParentAccordionChange}
                  onChildChange={handleChildAccordionChange}
                  onPreview={(file) => {
                    setPreviewFile(file);
                    handlePreviewFile(file);
                    setOpen(true);
                  }}
                  onDownload={handleDownloadFile} />
              </div>
              <div className="campaign-page-right-section">
                <div className="campaign-page-logo">
                  <img
                    src={resourceCampaignAllRecord[0]?.resourceImage || resourceAllRecord[0]?.imageContentHub?.items?.[0]?.contentFile?.path || undefined}
                    alt={resourceCampaignAllRecord[0]?.resourceTitle || resourceAllRecord[0]?.title}
                  />
                </div>
                <div
                  className="campaign-header-content-description"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(resourceCampaignAllRecord[0]?.resourceDesc || resourceAllRecord[0]?.shortDescription || '',
                )}}
                ></div>
                <div className="campaign-page-information-key-image">
                  <img
                    src={resourceTypeAllRecord[0]?.resourceTypeImage || `${REACT_APP_WUCA_PUBLIC_URL}/image_not_available.png`}
                    alt={resourceTypeAllRecord[0]?.resourceTypeTitle || ''}
                  />
                </div>
              </div>
            </div>
          </div>
          <ResourcePreviewDialog
            open={open}
            loading={loading}
            previewFile={previewFile}
            blobUrl={blobUrl}
            onClose={handleClosePreview}
          />
          <ScrollToHashElement />
          <AppSnackbar
            open={openSnackbar}
            severity={severity}
            message={messageSnackbar}
            onClose={handleSnackbarClose}
          />
        </>
      ) : (
        isResourceCalled === true ? (
          <NotFound />
        ) : (
          <></>
        )
      )}
    </>
  );
};

export default Resources;
